import { useState, useEffect } from "react"
import { supabase } from "@/services/supabase"
import { Button } from "@/components/ui/button"
import { Link, useLocation, Outlet } from "react-router-dom"
import { LayoutDashboard, Package, DollarSign, LogOut, Menu, X, ChevronRight, Store, TrendingUp, CreditCard, AlertCircle } from "lucide-react"
import { cn } from "@/lib/utils"
import posthog from 'posthog-js'
import { useAuth } from "@/hooks/useAuth"

export default function AdminLayout() {
    const location = useLocation()
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)
    const { data: auth, isLoading: authLoading } = useAuth()
    const subscriptionStatus = auth?.subscriptionStatus || null
    const currentPeriodEnd = auth?.currentPeriodEnd || null

    useEffect(() => {
        if (auth && import.meta.env.PROD) {
            // Identify user in PostHog
            posthog.identify(auth.id, {
                email: auth.email,
                subscription_status: auth.subscriptionStatus
            })
        }
    }, [auth])

    const handleLogout = async () => {
        // Clear all local storage and session data first
        localStorage.clear()
        // Fire and forget server-side signout
        supabase.auth.signOut().catch(() => { })
        // Hard redirect immediately to avoid flicker to /login
        window.location.href = '/'
    }

    const menuItems = [
        {
            label: "Início",
            icon: LayoutDashboard,
            path: "/dashboard"
        },
        {
            label: "Produtos",
            icon: Package,
            path: "/dashboard/produtos"
        },
        {
            label: "Materiais",
            icon: DollarSign,
            path: "/dashboard/materiais"
        },
        {
            label: "Resultados",
            icon: TrendingUp,
            path: "/dashboard/insights"
        },
        {
            label: "Meu Negócio",
            icon: Store,
            path: "/dashboard/negocio"
        },
        {
            label: "Minha conta",
            icon: CreditCard,
            path: "/dashboard/minha-conta"
        }
    ]

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen)
    const closeSidebar = () => setIsSidebarOpen(false)

    const isCanceled = subscriptionStatus === 'canceled'
    const isPastDue = subscriptionStatus === 'past_due'

    // Check if the period is actually over
    const isExpired = currentPeriodEnd ? new Date(currentPeriodEnd) < new Date() : false

    // User loses access only if status is canceled OR (past_due AND expired)
    const shouldBlockAccess = isCanceled || (isPastDue && isExpired)

    if (authLoading) {
        return (
            <div className="min-h-screen bg-[#FFF4EC] flex items-center justify-center">
                <div className="relative h-12 w-12">
                    <div className="absolute inset-0 rounded-xl border-4 border-pink-100"></div>
                    <div className="absolute inset-0 rounded-xl border-4 border-[#E86A92] border-t-transparent animate-spin"></div>
                </div>
            </div>
        )
    }

    // If we're here and no auth, ProtectedRoute will handle it, 
    // but we return null to be safe instead of a separate spinner.
    if (!auth) return null

    return (
        <div className="min-h-screen bg-[#FFF4EC] flex flex-col lg:flex-row">
            {/* Mobile Backdrop */}
            {isSidebarOpen && !shouldBlockAccess && (
                <div
                    className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
                    onClick={closeSidebar}
                />
            )}

            {/* Sidebar */}
            {!shouldBlockAccess && (
                <aside className={cn(
                    "fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-slate-100 transition-all duration-300 transform lg:translate-x-0 lg:sticky lg:top-0 lg:h-screen shadow-xl lg:shadow-none flex flex-col shrink-0",
                    isSidebarOpen ? "translate-x-0" : "-translate-x-full"
                )}>
                    {/* Logo Section */}
                    <div className="p-6 md:p-8">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-white rounded-xl flex items-center justify-center shadow-lg shadow-pink-200/50 rotate-3 border border-pink-100/50">
                                <img src="/icon.svg" alt="Logo" className="w-10 h-10" />
                            </div>
                            <div>
                                <span className="text-xl font-black italic tracking-tight text-[#E86A92] block leading-none">Cozinha Certa</span>
                                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mt-1 block">Painel Administrativo</span>
                            </div>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 px-4 space-y-2 mt-4">
                        {menuItems.map((item) => {
                            const isActive = location.pathname === item.path
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={closeSidebar}
                                >
                                    <Button
                                        variant="ghost"
                                        className={cn(
                                            "w-full justify-between gap-3 h-12 px-4 rounded-xl transition-all duration-200 group",
                                            isActive
                                                ? "bg-[#E86A92]/10 text-[#E86A92] shadow-sm shadow-[#E86A92]/5 font-bold"
                                                : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                                        )}
                                    >
                                        <div className="flex items-center gap-3">
                                            <item.icon size={20} className={cn(
                                                "transition-transform duration-200 group-hover:scale-110",
                                                isActive ? "text-[#E86A92]" : "text-slate-400"
                                            )} />
                                            <span>{item.label}</span>
                                        </div>
                                        {isActive && <ChevronRight size={14} className="opacity-50" />}
                                    </Button>
                                </Link>
                            )
                        })}
                    </nav>

                    {/* Footer Section */}
                    <div className="p-4 border-t border-slate-50 space-y-4">
                        <Button
                            variant="ghost"
                            className="w-full justify-start gap-3 h-12 px-4 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all group"
                            onClick={handleLogout}
                        >
                            <div className="h-8 w-8 rounded-lg bg-slate-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                                <LogOut size={18} />
                            </div>
                            <span className="font-medium">Encerrar Sessão</span>
                        </Button>

                        <div className="flex items-center justify-center gap-4 text-[10px] font-bold uppercase tracking-widest text-slate-300">
                            <Link to="/termos" className="hover:text-[#E86A92] transition-colors">Termos</Link>
                            <span>•</span>
                            <Link to="/privacidade" className="hover:text-[#E86A92] transition-colors">Privacidade</Link>
                        </div>
                    </div>
                </aside>
            )}

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col min-h-screen">
                {/* Global Payment Status Alert */}
                {subscriptionStatus === 'past_due' && (
                    <div className="bg-orange-500 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm font-bold animate-in slide-in-from-top duration-500">
                        <AlertCircle size={16} />
                        <span>Atenção: Pagamento pendente. Sua assinatura será suspensa em breve se não for regularizada.</span>
                        <Link
                            to="/dashboard/minha-conta"
                            className="bg-white text-orange-600 px-3 py-1 rounded-lg text-xs hover:bg-orange-50 transition-colors ml-2"
                        >
                            Regularizar Agora
                        </Link>
                    </div>
                )}

                {/* Top Header - Mobile Only (Hidden if blocked) */}
                {!shouldBlockAccess && (
                    <header className="h-20 bg-white border-b border-slate-100 px-6 flex items-center justify-between lg:hidden sticky top-0 z-30">
                        <div className="flex items-center gap-2">
                            <div className="h-8 w-8 bg-white rounded-lg flex items-center justify-center text-white rotate-3">
                                <img src="/icon.svg" alt="Logo" className="w-10 h-10" />
                            </div>
                            <span className="font-bold text-lg tracking-tight text-[#E86A92]">Cozinha Certa</span>
                        </div>

                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 text-slate-600 rounded-xl bg-slate-50"
                            onClick={toggleSidebar}
                            aria-label="Toggle Menu"
                        >
                            {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
                        </Button>
                    </header>
                )}

                {/* Page Content */}
                <main className={cn(
                    "flex-1 overflow-y-auto w-full",
                    shouldBlockAccess && "flex items-center justify-center"
                )}>
                    <div className="max-w-7xl mx-auto">
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    )
}
